<template>
  <div
    class="video-player"
    :class="{ 'is-fullscreen': isFullscreen }"
    @click="togglePlay"
    @mousemove="showControls"
    @mouseleave="hideControls"
  >
    <video
      ref="video"
      :src="src"
      :poster="poster"
      class="video-player__media"
      @click.stop
      @play="onPlay"
      @pause="onPause"
      @timeupdate="onTimeUpdate"
      @loadedmetadata="onLoadedMetadata"
      @volumechange="onVolumeChange"
      @ended="onEnded"
    ></video>

    <div
      v-if="!isPlaying && showBigPlay"
      class="video-player__big-play"
    >
      <svg
        viewBox="0 0 24 24"
        width="64"
        height="64"
      >
        <path
          fill="currentColor"
          d="M8 5v14l11-7z"
        />
      </svg>
    </div>

    <transition name="fade">
      <div
        v-show="controlsVisible"
        class="video-player__controls"
        @click.stop
      >
        <div
          class="video-player__progress"
          @click="seek"
          @mousedown="startScrub"
        >
          <div class="video-player__progress-buffered"></div>
          <div
            class="video-player__progress-filled"
            :style="{ width: progress + '%' }"
          ></div>
        </div>

        <div class="video-player__row">
          <button
            class="video-player__btn"
            @click="togglePlay"
            :title="isPlaying ? 'Пауза' : 'Воспроизвести'"
          >
            <svg
              v-if="!isPlaying"
              viewBox="0 0 24 24"
              width="22"
              height="22"
            >
              <path
                fill="currentColor"
                d="M8 5v14l11-7z"
              />
            </svg>
            <svg
              v-else
              viewBox="0 0 24 24"
              width="22"
              height="22"
            >
              <path
                fill="currentColor"
                d="M6 5h4v14H6zm8 0h4v14h-4z"
              />
            </svg>
          </button>

          <div class="video-player__volume">
            <button
              class="video-player__btn"
              @click="toggleMute"
              :title="isMuted ? 'Включить звук' : 'Выключить звук'"
            >
              <svg
                v-if="isMuted || volume === 0"
                viewBox="0 0 24 24"
                width="22"
                height="22"
              >
                <path
                  fill="currentColor"
                  d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.2.05-.41.05-.63zM19 12c0 .94-.2 1.82-.54 2.64l1.51 1.51C20.63 14.91 21 13.5 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06c1.38-.31 2.63-.95 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4L9.91 6.09 12 8.18V4z"
                />
              </svg>
              <svg
                v-else-if="volume < 0.5"
                viewBox="0 0 24 24"
                width="22"
                height="22"
              >
                <path
                  fill="currentColor"
                  d="M18.5 12c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM5 9v6h4l5 5V4L9 9H5z"
                />
              </svg>
              <svg
                v-else
                viewBox="0 0 24 24"
                width="22"
                height="22"
              >
                <path
                  fill="currentColor"
                  d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z"
                />
              </svg>
            </button>
            <input
              type="range"
              min="0"
              max="1"
              step="0.05"
              :value="volume"
              class="video-player__volume-slider"
              @input="setVolume($event.target.value)"
            />
          </div>

          <div class="video-player__time">
            {{ formatTime(currentTime) }} / {{ formatTime(duration) }}
          </div>

          <div class="video-player__spacer"></div>
          <button
            class="video-player__btn"
            @click="toggleFullscreen"
            :title="isFullscreen ? 'Выйти из полноэкранного режима' : 'Полноэкранный режим'"
          >
            <svg
              v-if="!isFullscreen"
              viewBox="0 0 24 24"
              width="22"
              height="22"
            >
              <path
                fill="currentColor"
                d="M7 14H5v5h5v-2H7v-3zm-2-4h2V7h3V5H5v5zm12 7h-3v2h5v-5h-2v3zM14 5v2h3v3h2V5h-5z"
              />
            </svg>
            <svg
              v-else
              viewBox="0 0 24 24"
              width="22"
              height="22"
            >
              <path
                fill="currentColor"
                d="M5 16h3v3h2v-5H5v2zm3-8H5v2h5V5H8v3zm6 11h2v-3h3v-2h-5v5zm2-11V5h-2v5h5V8h-3z"
              />
            </svg>
          </button>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
  export default {
    name: 'VideoPlayer',
    props: {
      src: {
        type: String,
        required: true
      },
      poster: {
        type: String,
        default: ''
      },
      autoplay: {
        type: Boolean,
        default: false
      }
    },
    data() {
      return {
        isPlaying: false,
        currentTime: 0,
        duration: 0,
        volume: 1,
        isMuted: false,
        isFullscreen: false,
        controlsVisible: true,
        showBigPlay: true,
        hideControlsTimer: null
      }
    },
    computed: {
      progress() {
        if (!this.duration) return 0
        return (this.currentTime / this.duration) * 100
      }
    },
    mounted() {
      const video = this.$refs.video
      if (this.autoplay) {
        video.play().catch(() => {})
      }
      document.addEventListener('fullscreenchange', this.onFullscreenChange)
    },
    beforeDestroy() {
      document.removeEventListener('fullscreenchange', this.onFullscreenChange)
      clearTimeout(this.hideControlsTimer)
    },
    methods: {
      togglePlay() {
        const video = this.$refs.video
        if (video.paused || video.ended) {
          video.play()
        } else {
          video.pause()
        }
        this.showBigPlay = false
      },
      onPlay() {
        this.isPlaying = true
        this.showBigPlay = false
      },
      onPause() {
        this.isPlaying = false
      },
      onEnded() {
        this.isPlaying = false
        this.showBigPlay = true
      },
      onTimeUpdate() {
        this.currentTime = this.$refs.video.currentTime
      },
      onLoadedMetadata() {
        this.duration = this.$refs.video.duration
      },
      onVolumeChange() {
        this.volume = this.$refs.video.volume
        this.isMuted = this.$refs.video.muted
      },
      seek(event) {
        const rect = event.currentTarget.getBoundingClientRect()
        const ratio = (event.clientX - rect.left) / rect.width
        this.$refs.video.currentTime = ratio * this.duration
      },
      startScrub(event) {
        this.seek(event)
      },
      setVolume(value) {
        const v = parseFloat(value)
        this.$refs.video.volume = v
        if (v > 0 && this.isMuted) {
          this.$refs.video.muted = false
        }
      },
      toggleMute() {
        this.$refs.video.muted = !this.$refs.video.muted
      },
      toggleFullscreen() {
        const el = this.$el
        if (!document.fullscreenElement) {
          ;(el.requestFullscreen || el.webkitRequestFullscreen || el.msRequestFullscreen).call(el)
        } else {
          ;(
            document.exitFullscreen ||
            document.webkitExitFullscreen ||
            document.msExitFullscreen
          ).call(document)
        }
      },
      onFullscreenChange() {
        this.isFullscreen = !!document.fullscreenElement
      },
      formatTime(seconds) {
        if (!isFinite(seconds)) return '0:00'
        const m = Math.floor(seconds / 60)
        const s = Math.floor(seconds % 60)
        return `${m}:${s < 10 ? '0' + s : s}`
      },
      showControls() {
        this.controlsVisible = true
        clearTimeout(this.hideControlsTimer)
        this.hideControlsTimer = setTimeout(() => {
          if (this.isPlaying) {
            this.controlsVisible = false
          }
        }, 2500)
      },
      hideControls() {
        if (this.isPlaying) {
          this.controlsVisible = false
        }
      }
    }
  }
</script>

<style scoped>
  .video-player {
    position: relative;
    width: 100%;
    max-width: 800px;
    background: #000;
    border-radius: 8px;
    overflow: hidden;
    cursor: pointer;
    user-select: none;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  }

  .video-player__media {
    display: block;
    width: 100%;
    height: auto;
  }

  .video-player__big-play {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    color: rgba(255, 255, 255, 0.85);
    pointer-events: none;
    text-shadow: 0 2px 10px rgba(0, 0, 0, 0.5);
  }

  .video-player__controls {
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    padding: 12px 14px 10px;
    background: linear-gradient(to top, rgba(0, 0, 0, 0.75), transparent);
    color: #fff;
  }

  .video-player__progress {
    position: relative;
    height: 4px;
    background: rgba(255, 255, 255, 0.25);
    border-radius: 2px;
    cursor: pointer;
    margin-bottom: 8px;
    transition: height 0.15s;
  }
  .video-player__progress:hover {
    height: 6px;
  }
  .video-player__progress-buffered {
    position: absolute;
    inset: 0;
    background: rgba(255, 255, 255, 0.15);
    border-radius: 2px;
  }
  .video-player__progress-filled {
    position: absolute;
    top: 0;
    left: 0;
    bottom: 0;
    background: #fab400;
    border-radius: 2px;
    pointer-events: none;
  }

  .video-player__row {
    display: flex;
    align-items: center;
    gap: 10px;
  }

  .video-player__btn {
    background: none;
    border: none;
    color: #fff;
    cursor: pointer;
    padding: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 4px;
    transition: background 0.15s;
  }
  .video-player__btn:hover {
    background: rgba(255, 255, 255, 0.15);
  }

  .video-player__volume {
    display: flex;
    align-items: center;
    gap: 6px;
  }
  .video-player__volume-slider {
    width: 80px;
    accent-color: #fab400;
    cursor: pointer;
  }

  .video-player__time {
    font-size: 13px;
    font-variant-numeric: tabular-nums;
    color: rgba(255, 255, 255, 0.9);
  }

  .video-player__spacer {
    flex: 1;
  }

  .fade-enter-active,
  .fade-leave-active {
    transition: opacity 0.25s;
  }
  .fade-enter,
  .fade-leave-to {
    opacity: 0;
  }

  /* Полноэкранный режим */
  .video-player.is-fullscreen {
    max-width: none;
    width: 100vw;
    height: 100vh;
    border-radius: 0;
  }
  .video-player.is-fullscreen .video-player__media {
    height: 100%;
    object-fit: contain;
  }
</style>
